/**
 * External network types for property sources
 * null = Tokko (native or network)
 * string = External network identifier
 */
export const EXTERNAL_NETWORKS = {
  OPENNAVENT: 'opennavent',
  QUINTOANDAR: 'quintoandar',
};

/**
 * Property origin types
 * Properties can be native (tokko) or imported from external sources (quintoandar, orulo, dwv)
 */
export const PROPERTY_ORIGINS = {
  TOKKO: 'tokko',
  QUINTOANDAR: 'quintoandar',
  ORULO: 'orulo',
  DWV: 'dwv',
};

/**
 * Partner platform configuration for imported developments/properties.
 * Each entry maps an origin to its display config.
 * To add a new platform (e.g. DWV), add a new entry here with the origin key.
 */
export const PARTNER_PLATFORM_CONFIG = {
  [PROPERTY_ORIGINS.ORULO]: {
    origin: PROPERTY_ORIGINS.ORULO,
    name: 'Órulo',
    seeDevelopmentKey: 'SeeDevelopmentInOrulo',
    badgeBackground: 'var(--oruloBlue)',
  },
  [PROPERTY_ORIGINS.DWV]: {
    origin: PROPERTY_ORIGINS.DWV,
    name: 'DWV',
    seeDevelopmentKey: 'SeeDevelopmentInDwv',
    badgeBackground: 'var(--dwvRed)',
  },
};

/**
 * Check if a property is from an external network
 * @param {string|null} outerNet - The outer network identifier
 * @returns {boolean}
 */
export const isExternalNetwork = (outerNet) =>
  outerNet !== null && outerNet !== undefined && outerNet !== '';

/**
 * Check if a property is from a specific external network
 * @param {string|null} outerNet - The outer network identifier
 * @param {string} network - The network to check against
 * @returns {boolean}
 */
export const isFromNetwork = (outerNet, network) => outerNet === network;

/**
 * Check if a property should display QuintoAndar layout
 * This is true for both external QuintoAndar properties AND imported QuintoAndar properties
 * @param {string|null} outerNet - The outer network identifier
 * @param {string|null} origin - The property origin (tokko or quintoandar)
 * @returns {boolean}
 */
export const shouldShowQuintoAndarLayout = (outerNet, origin) =>
  isFromNetwork(outerNet, EXTERNAL_NETWORKS.QUINTOANDAR) ||
  origin === PROPERTY_ORIGINS.QUINTOANDAR;

/**
 * Check if a property is imported from QuintoAndar (not external)
 * These are properties that exist in Tokko but were originally imported from QuintoAndar
 * @param {string|null} outerNet - The outer network identifier
 * @param {string|null} origin - The property origin (tokko or quintoandar)
 * @returns {boolean}
 */
export const isQuintoAndarImported = (outerNet, origin) =>
  !isExternalNetwork(outerNet) && origin === PROPERTY_ORIGINS.QUINTOANDAR;

/**
 * Check if a development/property is imported from a partner platform (Órulo, DWV in the future).
 * @param {string|null} outerNet - The outer network identifier
 * @param {string|null} origin - The property origin
 * @returns {boolean}
 */
export const isPartnerPlatformImported = (outerNet, origin) =>
  !isExternalNetwork(outerNet) &&
  origin !== null &&
  origin in PARTNER_PLATFORM_CONFIG;

/**
 * Get partner platform config for a given origin.
 * Returns null if the origin is not a partner platform.
 * @param {string|null} origin - The property origin
 * @returns {object|null}
 */
export const getPartnerPlatformConfig = (origin) =>
  PARTNER_PLATFORM_CONFIG[origin] || null;

export const usesOruloPartnerFilesApi = (origin) =>
  origin === PROPERTY_ORIGINS.ORULO;
